/**
 * AdmissionsExplorer.jsx
 * Veracross admissions tool — two tabs:
 *
 *   1. AI Query        — natural-language questions over admissions data,
 *                        LLM-planned endpoint fetches, PII-free analysis.
 *   2. Checklist Tracker — deterministic scan of per-application checklist
 *                        progress. Computes missing required items, renders
 *                        a sortable expandable table + count analytics.
 *                        No LLM in the core path.
 *
 * ── ARCHITECTURE NOTE ──────────────────────────────────────────────────────
 * The APPLICATION is the spine of this tool, not the applicant.
 *   - /admission/applications  IS filterable by year_applying_for and carries
 *     all pipeline fields (grade, status, pool, dates, decision). It also has
 *     NO name fields, so it is inherently PII-free.
 *   - /admission/applicants    is identity data. It is NOT year-filterable
 *     ("Unknown parameters were provided: year_applying_for"), so it cannot be
 *     the primary list. It is used only to resolve names.
 *
 * NAME RESOLUTION — set-driven early exit:
 *   /admission/applicants is returned in id order, not name order, so there is
 *   no way to "search and stop early" for one name. Instead, we collect the
 *   distinct applicant_ids that actually appear in the loaded applications,
 *   then stream the applicants list page by page, ticking ids off that set,
 *   and STOP as soon as every needed id is found. This fetches far less than a
 *   full applicant pull and never loads the entire historical applicant list.
 *
 * PII policy (mirrors CanvasAnalytics.jsx):
 *   - scrubPII() strips names/emails/phones/addresses/DOB BEFORE any data
 *     reaches the LLM.
 *   - applicant_id / application_id retained as join keys.
 *   - Name index used to rehydrate display text only. Raw Data + PDF stay
 *     anonymized. Checklist CSV DOES include names (internal worklist) and is
 *     labelled CONFIDENTIAL.
 *
 * Confirmed v3 admission endpoints (tested April 2026):
 *   /admission/applicants
 *   /admission/applications
 *   /admission/applications/{application_id}/checklists
 *   /admission/config/years/{year}/checklists
 *   /admission/relatives
 */

import { useState, useCallback, useRef, useMemo } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from "recharts";

// ─── PROXY HELPERS ────────────────────────────────────────────────────────────
const PROXY_BASE = process.env.REACT_APP_PROXY_URL ||
  "https://us-central1-kings-app-74847.cloudfunctions.net";

async function getFirebaseToken() {
  const auth = getAuth();
  if (auth.currentUser) return auth.currentUser.getIdToken();
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => reject(new Error("Auth timeout — please sign in.")), 5000);
    const unsubscribe = onAuthStateChanged(auth, user => {
      clearTimeout(timeout); unsubscribe();
      if (user) resolve(user.getIdToken());
      else reject(new Error("Not authenticated — please sign in."));
    });
  });
}

async function vcProxy(path, params = {}, pageNumber = 1, pageSize = 100) {
  const idToken = await getFirebaseToken();
  const query = new URLSearchParams({
    path,
    page_number: String(pageNumber),
    page_size:   String(pageSize),
  });
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    if (Array.isArray(v)) v.forEach(i => query.append(k, i));
    else query.set(k, v);
  }
  const res = await fetch(`${PROXY_BASE}/veracrossProxy?${query}`, {
    headers: { Authorization: `Bearer ${idToken}` },
  });
  const body = await res.json();
  if (!res.ok) throw new Error(body?.error || `VC proxy ${res.status}`);
  return body; // { data, pagination, tokenAgeMinutes }
}

async function proxyAnthropic(body) {
  const idToken = await getFirebaseToken();
  const res = await fetch(`${PROXY_BASE}/anthropicProxy`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Anthropic proxy ${res.status}: ${await res.text()}`);
  return res.json();
}

// ─── DESIGN TOKENS (shared with Canvas tool) ─────────────────────────────────
const FONT_DISPLAY = "'Playfair Display', Georgia, serif";
const FONT_DATA    = "'Rajdhani', 'Trebuchet MS', sans-serif";
const FONT_BODY    = "'EB Garamond', Georgia, serif";

const C = {
  ink:    "#1a1714",
  cream:  "#faf7f2",
  gold:   "#b8924a",
  navy:   "#1a3a5c",
  muted:  "#7a7068",
  border: "#e8e2d8",
  bg:     "#f4f0e8",
  green:  "#22863a",
  red:    "#8b2c2c",
};

const NAVY_P  = [26, 58, 92];
const GOLD_P  = [184, 146, 74];
const INK_P   = [26, 23, 20];
const MUTED_P = [122, 112, 104];
const GREEN_P = [34, 134, 58];

// ─── PII SCRUBBER ─────────────────────────────────────────────────────────────
function scrubPII(obj) {
  if (Array.isArray(obj)) return obj.map(scrubPII);
  if (obj && typeof obj === "object") {
    const allowed = new Set([
      // Stable numeric IDs — safe for cross-system joins
      "person_pk", "applicant_id", "application_id", "household_id",
      "relationship_id", "relative_id", "inquiry_id",
      "checklist_item_id", "student_id", "staff_id", "id",
      // Classification / status fields — enum IDs, no PII
      "application_status", "application_status_description",
      "application_decision_response", "application_decision_response_description",
      "candidate_pool", "candidate_pool_description",
      "admission_source", "admission_source_description",
      "grade_applying_for", "year_applying_for", "month_applying_for",
      "current_grade", "current_grade_description",
      "resident_status_applying_for", "resident_status_applying_for_description",
      "campus_applying_for", "campus_applying_for_description",
      "student_group_applying_for", "student_group_applying_for_description",
      "relationship", "relationship_description",
      "legal_custody", "admissions_access", "parent_portal_access",
      "requesting_financial_aid",
      // Checklist fields
      "checklist_type", "required", "required_for_application",
      "required_for_review", "applies_to_prospect", "completed",
      "follow_up_complete", "school_year", "abbreviation",
      "filter_by_from_grade", "filter_by_to_grade", "filter_by_campus",
      "filter_by_resident_status", "filter_by_international", "filter_by_group",
      "sort_key", "category",
      // Dates — not PII on their own
      "admission_lead_date", "inquiry_date", "visit_date",
      "application_date", "application_decision_date",
      "application_decision_response_date", "created_at", "updated_at",
      "scheduled_date", "completed_date", "follow_up_complete_date",
      "last_modified_date", "due_date", "early_due_date",
      // Non-identifying enums
      "gender", "pronouns", "ethnicity",
      "country", "country_description",
    ]);
    const blocked = new Set([
      "first_name", "middle_name", "last_name", "nick_name", "maiden_name",
      "name", "name_prefix", "name_suffix", "full_name", "display_name",
      "sortable_name", "preferred_name",
      "email", "email_personal", "email_school", "email_work",
      "phone", "phone_mobile", "phone_home", "phone_work", "phone_fax",
      "address_1", "address_2", "address_3", "street", "city",
      "state", "postal_code",
      "date_of_birth", "dob", "birth_date", "ssn", "national_id",
      "passport_number", "photo_url", "photo", "avatar_url",
      "notes", "comments", "bio",
    ]);
    const clean = {};
    for (const [k, v] of Object.entries(obj)) {
      if (blocked.has(k)) continue;
      if (allowed.has(k)) { clean[k] = v; continue; }
      clean[k] = scrubPII(v);
    }
    return clean;
  }
  return obj;
}

// Build a name record from a raw applicant object (called BEFORE scrubbing).
function nameRecordFromApplicant(obj) {
  const first = obj.first_name || "";
  const last  = obj.last_name  || "";
  const nick  = obj.nick_name  || "";
  const full  = obj.name || obj.full_name || obj.display_name;
  const assembled = full || `${first}${nick ? ` "${nick}"` : ""} ${last}`.trim();
  if (!assembled || assembled.length < 2) return null;
  return {
    name: assembled,
    firstName: first,
    lastName: last,
    grade: obj.grade_applying_for_description || obj.current_grade_description || null,
  };
}

// ─── VC API CLIENT ────────────────────────────────────────────────────────────
// Paginates an endpoint fully and returns the raw (UN-scrubbed) records.
//
// NOTE: Veracross does NOT reliably send page-count headers — the proxy's
// pagination.totalPages / totalRecords come back as 1 / 0. So we CANNOT trust
// totalPages to know when to stop. Instead we keep fetching until a page comes
// back with fewer than pageSize records — the universal "last page" signal.
// If the proxy ever does return a real totalPages, we honor it as an extra
// stop condition, but we never depend on it.
async function vcFetchAllRaw(path, params = {}, { pageSize = 100, maxPages = 60 } = {}) {
  let raw = [];
  let pageNumber = 1;
  while (pageNumber <= maxPages) {
    const { data, pagination } = await vcProxy(path, params, pageNumber, pageSize);
    const arr = Array.isArray(data) ? data : (data?.data || (data ? [data] : []));
    raw = raw.concat(arr);
    // Last page: a short (or empty) page means there is no more data.
    if (arr.length < pageSize) break;
    // Extra stop condition — only if the proxy gave us a real page count.
    if (pagination?.totalPages > 1 && pageNumber >= pagination.totalPages) break;
    pageNumber++;
  }
  return raw;
}

// Paginates fully and returns scrubbed records (for LLM-bound data).
async function vcFetchAll(path, params = {}, opts = {}) {
  const raw = await vcFetchAllRaw(path, params, opts);
  return { data: scrubPII(raw), totalCount: raw.length };
}

// Fetch one page only (used where a single record set is expected, e.g. nested
// per-application checklist lists).
async function vcFetchOne(path, params = {}) {
  const { data } = await vcProxy(path, params, 1, 100);
  return Array.isArray(data) ? data : (data?.data || (data ? [data] : []));
}

// Get the totalRecords count for an endpoint without pulling all pages.
// Veracross does not reliably send a count header — when it is absent the
// proxy reports totalRecords: 0. We return null in that case to mean
// "unknown" rather than falsely reporting zero.
async function vcCount(path, params = {}) {
  const { pagination } = await vcProxy(path, params, 1, 1);
  const n = pagination?.totalRecords;
  return (typeof n === "number" && n > 0) ? n : null;
}

/**
 * SET-DRIVEN NAME RESOLUTION
 * Given a Set of applicant_ids we need names for, stream /admission/applicants
 * page by page, removing ids from the set as they are found, and STOP as soon
 * as the set is empty. Returns { id -> nameRecord } for whatever was found.
 *
 * onProgress({ found, total, page }) is called after each page.
 */
async function resolveNamesForIds(neededIdSet, onProgress) {
  const remaining = new Set([...neededIdSet].map(String));
  const total = remaining.size;
  const nameMap = {};
  if (total === 0) return nameMap;

  let pageNumber = 1;
  const pageSize = 100;
  const maxPages = 200; // hard ceiling

  // We cannot trust pagination.totalPages (Veracross omits the header), so we
  // page until either every needed id is found (early exit) or a short page
  // signals the end of the applicant list.
  while (remaining.size > 0 && pageNumber <= maxPages) {
    const { data } = await vcProxy("/admission/applicants", {}, pageNumber, pageSize);
    const arr = Array.isArray(data) ? data : (data?.data || (data ? [data] : []));

    for (const rec of arr) {
      const id = String(rec.applicant_id || rec.person_pk || rec.id || "");
      if (id && remaining.has(id)) {
        const nr = nameRecordFromApplicant(rec);
        if (nr) nameMap[id] = nr;
        remaining.delete(id);
      }
    }
    onProgress?.({ found: total - remaining.size, total, page: pageNumber });
    if (remaining.size === 0) break;     // EARLY EXIT — every needed id found
    if (arr.length < pageSize) break;    // LAST PAGE — no more applicants
    pageNumber++;
  }
  return nameMap;
}

// ─── FUZZY MATCHING ───────────────────────────────────────────────────────────
function similarity(a, b) {
  a = a.toLowerCase().trim();
  b = b.toLowerCase().trim();
  if (a === b) return 1;
  if (a.length < 2 || b.length < 2) return 0;
  const trigrams = s => {
    const set = new Set();
    const padded = `  ${s}  `;
    for (let i = 0; i < padded.length - 2; i++) set.add(padded.slice(i, i + 3));
    return set;
  };
  const ta = trigrams(a), tb = trigrams(b);
  let intersection = 0;
  ta.forEach(t => { if (tb.has(t)) intersection++; });
  return (2 * intersection) / (ta.size + tb.size);
}

function detectNames(queryText, nameIndex) {
  const words = queryText.split(/\s+/);
  const people = Object.entries(nameIndex);
  const detections = [];
  for (let len = 3; len >= 1; len--) {
    for (let i = 0; i <= words.length - len; i++) {
      const phrase = words.slice(i, i + len).join(" ");
      if (phrase.length < 3) continue;
      const alreadyCovered = detections.some(d =>
        queryText.indexOf(phrase) >= queryText.indexOf(d.original) &&
        queryText.indexOf(phrase) + phrase.length <= queryText.indexOf(d.original) + d.original.length
      );
      if (alreadyCovered) continue;
      const matches = people
        .map(([id, p]) => ({ id, name: p.name, score: similarity(phrase, p.name) }))
        .filter(m => m.score >= 0.6)
        .sort((a, b) => b.score - a.score)
        .slice(0, 4);
      if (matches.length > 0) detections.push({ original: phrase, matches });
    }
  }
  return detections;
}

function dehydrateQuery(queryText, resolutions) {
  let result = queryText;
  for (const [original, id] of Object.entries(resolutions)) {
    result = result.replace(
      new RegExp(original.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi"),
      `applicant ${id}`
    );
  }
  return result;
}

function rehydrateText(text, nameIndex) {
  let result = text;
  for (const [id, person] of Object.entries(nameIndex)) {
    const patterns = [
      new RegExp(`\\bapplicant\\s+${id}\\b`, "gi"),
      new RegExp(`\\bapplication\\s+${id}\\b`, "gi"),
      new RegExp(`\\bperson\\s+${id}\\b`, "gi"),
      new RegExp(`\\bID\\s+${id}\\b`, "gi"),
      new RegExp(`\\b${id}\\b`, "g"),
    ];
    patterns.forEach(p => { result = result.replace(p, () => person.name); });
  }
  return result;
}

// ─── LLM: QUERY PLANNER ───────────────────────────────────────────────────────
async function planVCQuery(userQuery, filters) {
  const filterSummary = Object.entries(filters)
    .filter(([, v]) => v)
    .map(([k, v]) => `${k}=${v}`)
    .join(", ") || "none";

  const system = `You are a Veracross admissions data analyst. Given a natural-language question, respond ONLY with valid JSON — no markdown, no preamble:
{
  "endpoints": [
    { "path": "/admission/applications", "params": {}, "label": "short description" }
  ],
  "analysisInstructions": "what to do with the data"
}

Current user-set filters: ${filterSummary}. Apply these as params where relevant.

Available paths (Veracross v3 admissions — use these EXACT slash-separated paths):
  /admission/applications   — the PRIMARY pipeline endpoint. Filterable by
                              year_applying_for, application_status, candidate_pool,
                              grade_applying_for. Carries dates and decision fields.
                              Prefer this for almost all pipeline questions.
  /admission/applicants     — identity records. NOT filterable by year_applying_for.
                              Only use this if the question is specifically about
                              applicant identity attributes, not pipeline status.
  /admission/relatives      — parent/guardian/sibling records linked to applicants.

Common filter params (applications only):
  year_applying_for=<YYYY>
  application_status=<enum_id>
  candidate_pool=<enum_id>
  grade_applying_for=<int>

Do NOT put year_applying_for on /admission/applicants — it will be rejected.
Per-applicant checklist data is handled by a separate Checklist Tracker tool;
do not emit checklist endpoints here.

Return ONLY the JSON object.`;

  const resp = await proxyAnthropic({
    model: "claude-sonnet-4-6",
    max_tokens: 1000,
    system,
    messages: [{ role: "user", content: userQuery }],
  });
  const text = resp.content?.find(b => b.type === "text")?.text || "{}";
  return JSON.parse(text.replace(/```json|```/g, "").trim());
}

// ─── LLM: DATA ANALYST ────────────────────────────────────────────────────────
async function analyzeVCData(userQuery, fetchedData, history, includeCharts = false) {
  const chartInstruction = includeCharts ? `
When a chart would help illustrate the data, include it by placing [chart] in your narrative where it should appear, and append a JSON block at the end of your response in this exact format:
CHARTS_JSON:{"charts":[{"type":"bar","title":"Chart Title","labels":["A","B"],"datasets":[{"label":"Count","data":[12,8]}]}]}
Chart types: "bar", "line", "pie". Only include charts when they genuinely add value.` : "";

  const system = `You are an expert admissions analyst for a K-12 international school.
All data is fully anonymized — applicants appear ONLY as numeric IDs (applicant_id, application_id).
Status, pool, grade, and decision fields are present as IDs and their _description counterparts.
Be specific, data-driven, and concise. Note patterns, pipeline health, conversion rates, timing trends, and concerns where relevant.
Never fabricate names or invent details not in the data.
Use plain text with clear structure. No markdown bold (**) or bullet symbols.${chartInstruction}`;

  const messages = [
    ...history.slice(-6).map(h => ({ role: h.role, content: h.content })),
    {
      role: "user",
      content: `Question: ${userQuery}\n\nAnonymized Veracross data (no PII):\n${JSON.stringify(fetchedData, null, 2).slice(0, 50000)}`,
    },
  ];

  const resp = await proxyAnthropic({
    model: "claude-sonnet-4-6",
    max_tokens: 2000,
    system,
    messages,
  });
  const raw = resp.content?.find(b => b.type === "text")?.text || "No response.";

  let narrative = raw, charts = [];
  const m = raw.match(/CHARTS_JSON:(\{.*\})\s*$/s);
  if (m) {
    try {
      const parsed = JSON.parse(m[1]);
      charts = parsed.charts || [];
      narrative = raw.replace(/CHARTS_JSON:.*$/s, "").trim();
    } catch { /* ignore */ }
  }
  return { narrative, charts };
}

// ─── PDF HELPERS ──────────────────────────────────────────────────────────────
const getJSPDF = async () => {
  if (window.jspdf?.jsPDF) return window.jspdf.jsPDF;
  await new Promise((res, rej) => {
    const s = document.createElement("script");
    s.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
    s.onload = res; s.onerror = rej;
    document.head.appendChild(s);
  });
  if (!window.jspdf?.jsPDF) throw new Error("jsPDF failed to load");
  return window.jspdf.jsPDF;
};
const getHtml2Canvas = async () => {
  if (window.html2canvas) return window.html2canvas;
  await new Promise((res, rej) => {
    const s = document.createElement("script");
    s.src = "https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js";
    s.onload = res; s.onerror = rej;
    document.head.appendChild(s);
  });
  if (!window.html2canvas) throw new Error("html2canvas failed to load");
  return window.html2canvas;
};

function addContinuationHeader(doc, W, title) {
  doc.setFillColor(...NAVY_P); doc.rect(0, 0, W, 12, "F");
  doc.setFillColor(...GOLD_P); doc.rect(0, 12, W, 0.5, "F");
  doc.setTextColor(212, 172, 106); doc.setFontSize(7); doc.setFont("helvetica", "bold");
  doc.text(`${title.slice(0, 80)}${title.length > 80 ? "…" : ""}  (continued)`, W / 2, 8, { align: "center" });
}

function addFooters(doc, W, H, label = "Admissions Intelligence") {
  const pc = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pc; i++) {
    doc.setPage(i);
    doc.setFillColor(244, 240, 232); doc.rect(0, H - 10, W, 10, "F");
    doc.setFillColor(...GOLD_P); doc.rect(0, H - 10, W, 0.4, "F");
    doc.setFontSize(7); doc.setFont("helvetica", "italic"); doc.setTextColor(...MUTED_P);
    doc.text(`Internal use only · King's Academy ${label} · PII-Free Report`, W / 2, H - 4, { align: "center" });
    doc.text(`${i} / ${pc}`, W - 14, H - 4);
  }
}

async function buildQueryPage(doc, W, H, entry, idx, total, cardNode) {
  const now = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  doc.setFillColor(...NAVY_P); doc.rect(0, 0, W, 38, "F");
  doc.setFillColor(...GOLD_P); doc.rect(0, 38, W, 1, "F");
  doc.setTextColor(212, 172, 106); doc.setFontSize(7); doc.setFont("helvetica", "bold");
  doc.text("KING'S ACADEMY  ·  ADMISSIONS INTELLIGENCE  ·  INTERNAL USE ONLY", W / 2, 10, { align: "center" });
  if (total > 1) doc.text(`Query ${idx + 1} of ${total}`, W - 14, 10, { align: "right" });

  doc.setTextColor(200, 191, 176); doc.setFontSize(7.5); doc.setFont("helvetica", "italic");
  doc.text(`Veracross Admissions  ·  ${now}`, W / 2, 17, { align: "center" });

  doc.setTextColor(250, 247, 242); doc.setFontSize(12); doc.setFont("helvetica", "bold");
  const queryLines = doc.splitTextToSize(entry.query, W - 28);
  doc.text(queryLines, W / 2, queryLines.length > 1 ? 25 : 28, { align: "center" });

  doc.setFillColor(13, 45, 14);
  doc.roundedRect(12, 43, W - 24, 9, 1, 1, "F");
  doc.setDrawColor(...GREEN_P);
  doc.roundedRect(12, 43, W - 24, 9, 1, 1, "S");
  doc.setTextColor(63, 185, 80); doc.setFontSize(6.5); doc.setFont("helvetica", "bold");
  doc.text("PRIVACY NOTICE: Names, emails, phones, addresses, DOB stripped before analysis. VC IDs retained for system joins only.", W / 2, 48.5, { align: "center", maxWidth: W - 28 });

  const dataKeys = Object.keys(entry.data);
  const boxCount = Math.min(dataKeys.length, 4);
  const boxW = (W - 24 - (boxCount - 1) * 4) / Math.max(boxCount, 1);
  let bx = 12;
  dataKeys.slice(0, 4).forEach(k => {
    const count = Array.isArray(entry.data[k]) ? entry.data[k].length : 1;
    const labelLines = doc.splitTextToSize(k, boxW - 4);
    const boxH = 14 + Math.max(0, labelLines.length - 1) * 4;
    doc.setFillColor(244, 240, 232); doc.roundedRect(bx, 57, boxW, boxH, 1, 1, "F");
    doc.setDrawColor(...GOLD_P); doc.roundedRect(bx, 57, boxW, boxH, 1, 1, "S");
    doc.setTextColor(...INK_P); doc.setFontSize(11); doc.setFont("helvetica", "bold");
    doc.text(String(count), bx + boxW / 2, 65, { align: "center" });
    doc.setFontSize(5.5); doc.setFont("helvetica", "normal"); doc.setTextColor(...MUTED_P);
    doc.text(labelLines, bx + boxW / 2, 69, { align: "center" });
    bx += boxW + 4;
  });

  let y = 78;

  const chartNodes = cardNode?.querySelectorAll?.(".chart-capture-zone > div") || [];
  for (const chartEl of chartNodes) {
    try {
      const h2c = await getHtml2Canvas();
      const canvas = await h2c(chartEl, { scale: 2, backgroundColor: "#f4f0e8", useCORS: true, logging: false });
      const imgW = W - 24;
      const imgH = (imgW / canvas.width) * canvas.height;
      if (y + imgH > H - 20) { doc.addPage(); addContinuationHeader(doc, W, entry.query); y = 20; }
      doc.addImage(canvas.toDataURL("image/png"), "PNG", 12, y, imgW, imgH);
      y += imgH + 8;
    } catch (e) { console.warn("Chart capture failed:", e.message); }
  }

  if (y > H - 50) { doc.addPage(); addContinuationHeader(doc, W, entry.query); y = 20; }

  doc.setFillColor(...GOLD_P); doc.rect(12, y, 2.5, 6, "F");
  doc.setFontSize(8); doc.setFont("helvetica", "bold"); doc.setTextColor(...NAVY_P);
  doc.text("AI ANALYSIS  —  ANONYMIZED DATA ONLY", 18, y + 4.5);
  y += 12;

  const cleanAnalysis = (entry.analysis || "")
    .replace(/\*\*(.+?)\*\*/g, "$1").replace(/\*(.+?)\*/g, "$1").trim();
  const paragraphs = cleanAnalysis.split(/\n\n+/);
  doc.setFontSize(9); doc.setFont("helvetica", "normal"); doc.setTextColor(...INK_P);
  for (const para of paragraphs) {
    const lines = doc.splitTextToSize(para.trim(), W - 28);
    for (const line of lines) {
      if (y > H - 20) { doc.addPage(); addContinuationHeader(doc, W, entry.query); y = 20; }
      doc.text(line, 14, y);
      y += 5;
    }
    y += 3;
  }

  y += 4;
  if (y > H - 40) { doc.addPage(); addContinuationHeader(doc, W, entry.query); y = 20; }

  doc.setFillColor(...NAVY_P); doc.rect(12, y, 2.5, 6, "F");
  doc.setFontSize(8); doc.setFont("helvetica", "bold"); doc.setTextColor(...NAVY_P);
  doc.text("DATA FETCHED FROM VERACROSS (ANONYMIZED)", 18, y + 4.5);
  y += 12;

  for (const [label, records] of Object.entries(entry.data)) {
    if (y > H - 30) { doc.addPage(); addContinuationHeader(doc, W, entry.query); y = 20; }
    doc.setFillColor(244, 240, 232); doc.rect(12, y, W - 24, 8, "F");
    doc.setFontSize(8); doc.setFont("helvetica", "bold"); doc.setTextColor(...INK_P);
    doc.text(`${label}  —  ${Array.isArray(records) ? records.length : 1} record(s)`, 15, y + 5.5);
    y += 11;

    const sample = Array.isArray(records) ? records.slice(0, 3) : [records];
    for (const rec of sample) {
      if (y > H - 20) { doc.addPage(); addContinuationHeader(doc, W, entry.query); y = 20; }
      const str = JSON.stringify(rec);
      const compact = str.length > 140 ? str.slice(0, 137) + "…" : str;
      doc.setFontSize(6.5); doc.setFont("helvetica", "normal"); doc.setTextColor(...MUTED_P);
      doc.text(compact, 15, y, { maxWidth: W - 30 });
      y += 5;
    }
    if (Array.isArray(records) && records.length > 3) {
      doc.setFontSize(6.5); doc.setFont("helvetica", "italic"); doc.setTextColor(...MUTED_P);
      doc.text(`  … and ${records.length - 3} more record(s)`, 15, y);
      y += 5;
    }
    y += 4;
  }
}

// ─── CHECKLIST: CSV EXPORT ────────────────────────────────────────────────────
// NOTE: this CSV intentionally INCLUDES applicant names — it is an internal
// worklist that needs them to be actionable. Names are rehydrated locally and
// never passed through the LLM. The file is labelled CONFIDENTIAL.
function downloadChecklistCSV(rows, nameIndex, yearLabel) {
  const headers = [
    "Applicant Name", "Applicant ID", "Application ID",
    "Grade Applying For", "Application Status", "Missing Required Count",
    "Missing Items",
  ];
  const escape = (val) => {
    const s = String(val ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [headers.join(",")];
  for (const r of rows) {
    const name = nameIndex[String(r.applicantId)]?.name || `Applicant ${r.applicantId}`;
    const missingNames = r.missing.map(m => m.description).join(" | ");
    lines.push([
      escape(name),
      escape(r.applicantId),
      escape(r.applicationId),
      escape(r.gradeApplyingFor),
      escape(r.statusDescription || r.status),
      escape(r.missing.length),
      escape(missingNames),
    ].join(","));
  }
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `KA_Admissions_Checklist_CONFIDENTIAL_${yearLabel}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─── DISAMBIGUATION DIALOG ────────────────────────────────────────────────────
function DisambiguationDialog({ detections, nameIndex, onConfirm, onCancel }) {
  const [selections, setSelections] = useState(() => {
    const init = {};
    detections.forEach(d => { init[d.original] = d.matches[0]?.id || "__none__"; });
    return init;
  });

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(26,23,20,0.55)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 4, maxWidth: 540, width: "100%", boxShadow: "0 8px 40px rgba(0,0,0,0.18)", overflow: "hidden" }}>
        <div style={{ background: C.navy, padding: "16px 22px" }}>
          <div style={{ fontFamily: FONT_DATA, fontSize: 9, fontWeight: 600, letterSpacing: "0.2em", textTransform: "uppercase", color: C.gold, marginBottom: 4 }}>Name Disambiguation</div>
          <div style={{ fontFamily: FONT_DISPLAY, fontSize: "1rem", color: C.cream }}>We found some ambiguity in your query</div>
        </div>
        <div style={{ padding: "20px 22px", maxHeight: 420, overflowY: "auto" }}>
          {detections.map(d => (
            <div key={d.original} style={{ marginBottom: 20 }}>
              <div style={{ fontFamily: FONT_DATA, fontSize: 11, fontWeight: 600, color: C.ink, marginBottom: 8 }}>
                <span style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 3, padding: "2px 8px", fontStyle: "italic" }}>"{d.original}"</span>
                <span style={{ color: C.muted, fontWeight: 400, marginLeft: 8 }}>— did you mean:</span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {d.matches.map(m => {
                  const person = nameIndex[m.id];
                  const isSelected = selections[d.original] === m.id;
                  return (
                    <label key={m.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", border: `1px solid ${isSelected ? C.navy : C.border}`, borderRadius: 3, background: isSelected ? "#f0f4f9" : C.cream, cursor: "pointer" }}>
                      <input type="radio" name={d.original} value={m.id} checked={isSelected}
                        onChange={() => setSelections(s => ({ ...s, [d.original]: m.id }))}
                        style={{ accentColor: C.navy }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontFamily: FONT_DATA, fontSize: 13, fontWeight: 600, color: C.ink }}>{m.name}</div>
                        {person && (
                          <div style={{ fontFamily: FONT_DATA, fontSize: 10, color: C.muted }}>
                            Applicant{person.grade ? ` · applying for ${person.grade}` : ""} · ID: {m.id}
                          </div>
                        )}
                      </div>
                      <div style={{ fontFamily: FONT_DATA, fontSize: 10, color: C.muted }}>{Math.round(m.score * 100)}% match</div>
                    </label>
                  );
                })}
                <label style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", border: `1px solid ${selections[d.original] === "__none__" ? C.gold : C.border}`, borderRadius: 3, background: selections[d.original] === "__none__" ? "#fffaf0" : C.cream, cursor: "pointer" }}>
                  <input type="radio" name={d.original} value="__none__"
                    checked={selections[d.original] === "__none__"}
                    onChange={() => setSelections(s => ({ ...s, [d.original]: "__none__" }))}
                    style={{ accentColor: C.gold }} />
                  <div style={{ fontFamily: FONT_DATA, fontSize: 12, color: C.muted, fontStyle: "italic" }}>Not a person — search anyway</div>
                </label>
              </div>
            </div>
          ))}
        </div>
        <div style={{ padding: "14px 22px", borderTop: `1px solid ${C.border}`, display: "flex", justifyContent: "flex-end", gap: 10, background: C.bg }}>
          <button onClick={onCancel} style={{ fontFamily: FONT_DATA, fontSize: 12, fontWeight: 600, padding: "7px 18px", border: `1px solid ${C.border}`, borderRadius: 2, background: C.cream, color: C.muted, cursor: "pointer" }}>
            Cancel
          </button>
          <button onClick={() => onConfirm(selections)} style={{ fontFamily: FONT_DATA, fontSize: 12, fontWeight: 600, padding: "7px 18px", border: "none", borderRadius: 2, background: C.navy, color: C.cream, cursor: "pointer" }}>
            Confirm & Run Query →
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── WARNING DIALOG (long checklist scan) ─────────────────────────────────────
function ScanWarningDialog({ years, estimatedCount, onConfirm, onCancel }) {
  const estMinutes = estimatedCount ? Math.ceil((estimatedCount / 5) * 0.7 / 60) : null;
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(26,23,20,0.55)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 4, maxWidth: 480, width: "100%", boxShadow: "0 8px 40px rgba(0,0,0,0.18)", overflow: "hidden" }}>
        <div style={{ background: C.red, padding: "16px 22px" }}>
          <div style={{ fontFamily: FONT_DATA, fontSize: 9, fontWeight: 600, letterSpacing: "0.2em", textTransform: "uppercase", color: "#f4d4d4", marginBottom: 4 }}>Long Operation Warning</div>
          <div style={{ fontFamily: FONT_DISPLAY, fontSize: "1rem", color: "#fff" }}>This scan may take a while</div>
        </div>
        <div style={{ padding: "20px 22px", fontFamily: FONT_BODY, fontSize: 14, color: C.ink, lineHeight: 1.7 }}>
          You've selected {years.length} year{years.length > 1 ? "s" : ""} ({years.join(", ")}).
          The checklist scan fetches checklist progress one application at a time, so scanning
          multiple years (or any past year) means hundreds of API calls and can take several minutes.
          <div style={{ marginTop: 12, padding: "10px 14px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 3, fontFamily: FONT_DATA, fontSize: 12, color: C.muted }}>
            Estimated applications to scan: <strong style={{ color: C.ink }}>{estimatedCount || "unknown"}</strong>
            <br />
            Estimated time: <strong style={{ color: C.ink }}>{estMinutes ? `~${estMinutes} min` : "several minutes"}</strong>
          </div>
        </div>
        <div style={{ padding: "14px 22px", borderTop: `1px solid ${C.border}`, display: "flex", justifyContent: "flex-end", gap: 10, background: C.bg }}>
          <button onClick={onCancel} style={{ fontFamily: FONT_DATA, fontSize: 12, fontWeight: 600, padding: "7px 18px", border: `1px solid ${C.border}`, borderRadius: 2, background: C.cream, color: C.muted, cursor: "pointer" }}>
            Cancel
          </button>
          <button onClick={onConfirm} style={{ fontFamily: FONT_DATA, fontSize: 12, fontWeight: 600, padding: "7px 18px", border: "none", borderRadius: 2, background: C.navy, color: C.cream, cursor: "pointer" }}>
            Run Scan Anyway →
          </button>
        </div>
      </div>
    </div>
  );
}

const CHART_COLORS = ["#1a3a5c", "#b8924a", "#22863a", "#8b2c2c", "#6b5b95", "#4a90a4", "#d4a853", "#2d6a4f"];

// ─── CHART BLOCK ──────────────────────────────────────────────────────────────
function ChartBlock({ chart }) {
  if (!chart) return null;
  const { type, title, labels = [], datasets = [] } = chart;
  const chartData = labels.map((label, i) => {
    const point = { label };
    datasets.forEach(ds => { point[ds.label || "value"] = ds.data?.[i] ?? 0; });
    return point;
  });
  const pieData = labels.map((label, i) => ({ name: label, value: datasets[0]?.data?.[i] ?? 0 }));
  const rotateTicks = labels.length > 5;

  return (
    <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 3, padding: "16px 20px", marginBottom: 16 }}>
      {title && (
        <div style={{ fontFamily: FONT_DATA, fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: C.navy, marginBottom: 12 }}>
          {title}
        </div>
      )}
      <ResponsiveContainer width="100%" height={type === "pie" ? 380 : labels.length > 5 ? 340 : 280}>
        {type === "pie" ? (
          <PieChart>
            <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="42%" outerRadius={100} label={false}>
              {pieData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
            </Pie>
            <Tooltip formatter={(value) => {
              const total = pieData.reduce((s, d) => s + d.value, 0);
              return [`${value} (${total ? ((value / total) * 100).toFixed(1) : 0}%)`, ""];
            }} />
            <Legend verticalAlign="bottom" layout="vertical" align="center"
              wrapperStyle={{ fontFamily: FONT_DATA, fontSize: 12, paddingTop: 8 }}
            />
          </PieChart>
        ) : type === "line" ? (
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
            <XAxis dataKey="label" tick={{ fontFamily: FONT_DATA, fontSize: 10 }} angle={rotateTicks ? -35 : 0} textAnchor={rotateTicks ? "end" : "middle"} interval={0} height={rotateTicks ? 60 : 30} />
            <YAxis tick={{ fontFamily: FONT_DATA, fontSize: 10 }} />
            <Tooltip /><Legend />
            {datasets.map((ds, i) => (
              <Line key={i} type="monotone" dataKey={ds.label || "value"} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} dot={false} />
            ))}
          </LineChart>
        ) : (
          <BarChart data={chartData} margin={{ bottom: rotateTicks ? 40 : 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
            <XAxis dataKey="label" tick={{ fontFamily: FONT_DATA, fontSize: 10, fill: C.muted }} angle={rotateTicks ? -35 : 0} textAnchor={rotateTicks ? "end" : "middle"} interval={0} height={rotateTicks ? 60 : 30} />
            <YAxis tick={{ fontFamily: FONT_DATA, fontSize: 10 }} />
            <Tooltip />
            <Legend verticalAlign="bottom" wrapperStyle={{ fontFamily: FONT_DATA, fontSize: 11 }} />
            {datasets.map((ds, i) => (
              <Bar key={i} dataKey={ds.label || "value"} fill={CHART_COLORS[i % CHART_COLORS.length]} />
            ))}
          </BarChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}

// ─── BROWSE ROW (one application) ─────────────────────────────────────────────
function ApplicationRow({ app, nameIndex }) {
  const applicantId = app.applicant_id;
  const name = nameIndex[String(applicantId)]?.name || `Applicant ${applicantId}`;
  const status = app.application_status_description ?? app.application_status ?? "—";
  const grade  = app.grade_applying_for_description  ?? app.grade_applying_for  ?? "—";
  const year   = app.year_applying_for ?? "—";
  const pool   = app.candidate_pool_description ?? app.candidate_pool ?? "—";
  const appDate = app.application_date || app.inquiry_date || "—";

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr 0.6fr 0.6fr 1fr 0.9fr", gap: 12, padding: "10px 14px", borderBottom: `1px solid ${C.border}`, fontFamily: FONT_DATA, fontSize: 12, alignItems: "center" }}>
      <div>
        <div style={{ fontWeight: 600, color: C.ink }}>{name}</div>
        <div style={{ fontSize: 10, color: C.muted }}>Applicant {applicantId} · App {app.application_id}</div>
      </div>
      <div style={{ color: C.ink }}>{status}</div>
      <div style={{ color: C.ink }}>{grade}</div>
      <div style={{ color: C.ink }}>{year}</div>
      <div style={{ color: C.muted }}>{pool}</div>
      <div style={{ color: C.muted, fontSize: 11 }}>{appDate}</div>
    </div>
  );
}

// ─── RESULT CARD (AI Query tab) ───────────────────────────────────────────────
function ResultCard({ entry, idx, total, onDownload, pdfLoading, cardRef }) {
  const [tab, setTab] = useState("analysis");
  const recordCount = Object.values(entry.data).reduce((a, v) => a + (Array.isArray(v) ? v.length : 1), 0);

  return (
    <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 3, boxShadow: "0 2px 12px rgba(0,0,0,0.07)", overflow: "hidden", marginBottom: 24 }}>
      <div style={{ padding: "14px 22px 12px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 10, background: C.bg }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: FONT_DATA, fontSize: 9, fontWeight: 600, letterSpacing: "0.18em", textTransform: "uppercase", color: C.muted, marginBottom: 4 }}>
            Query {idx + 1} of {total}  ·  {recordCount} records  ·  Admissions
          </div>
          <h2 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.05rem", fontWeight: 600, margin: "0 0 6px", color: C.ink }}>{entry.query}</h2>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {Object.entries(entry.data).map(([label, recs]) => (
              <span key={label} style={{ fontFamily: FONT_DATA, fontSize: 10, padding: "2px 8px", background: C.cream, border: `1px solid ${C.border}`, borderRadius: 10, color: C.muted }}>
                {label}: {Array.isArray(recs) ? recs.length : 1}
              </span>
            ))}
          </div>
        </div>
        <button onClick={() => onDownload(idx)} disabled={pdfLoading}
          style={{ fontFamily: FONT_DATA, fontSize: 11, fontWeight: 600, padding: "7px 16px", border: `1px solid ${C.navy}`, borderRadius: 2, background: pdfLoading ? C.muted : C.navy, color: C.cream, flexShrink: 0, cursor: "pointer" }}>
          {pdfLoading ? "Building…" : "↓ Export PDF"}
        </button>
      </div>

      <div style={{ display: "flex", borderBottom: `1px solid ${C.border}` }}>
        {[["analysis", "AI Analysis"], ["data", "Raw Data (Anonymized)"]].map(([t, label]) => (
          <button key={t} onClick={() => setTab(t)}
            style={{ flex: 1, padding: "9px 0", fontFamily: FONT_DATA, fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", border: "none", background: tab === t ? C.cream : "#fff", color: tab === t ? C.ink : C.muted, borderBottom: tab === t ? `2px solid ${C.navy}` : "none", cursor: "pointer" }}>
            {label}
          </button>
        ))}
      </div>

      <div ref={cardRef} style={{ padding: "20px 24px", background: "#fff" }}>
        {tab === "analysis" && (
          <div>
            <div style={{ background: "#0d2d0e", border: "1px solid #238636", borderRadius: 2, padding: "8px 12px", marginBottom: 16, fontFamily: FONT_DATA, fontSize: "0.75rem", color: "#3fb950", display: "flex", gap: 8 }}>
              <span>🔒</span>
              <span>Applicant names, contact info and DOB stripped before LLM analysis. Names shown below are rehydrated locally for readability only.</span>
            </div>
            {entry.charts?.length > 0 && (
              <div className="chart-capture-zone">
                {entry.charts.map((chart, i) => <ChartBlock key={i} chart={chart} />)}
              </div>
            )}
            <div style={{ fontFamily: FONT_BODY, fontSize: 14, lineHeight: 1.8, color: C.ink, whiteSpace: "pre-wrap" }}>
              {(entry.rehydratedAnalysis || entry.analysis)?.replace(/\[chart\]/g, "").trim()}
            </div>
          </div>
        )}
        {tab === "data" && (
          <pre style={{ margin: 0, fontSize: "0.72rem", fontFamily: FONT_DATA, background: "#f8f6f2", border: `1px solid ${C.border}`, borderRadius: 2, padding: "1rem", overflowX: "auto", maxHeight: 400, overflowY: "auto", color: C.ink, lineHeight: 1.6 }}>
            {JSON.stringify(entry.data, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
}

// ─── CHECKLIST: APPLICANT ROW (expandable) ───────────────────────────────────
function ChecklistRow({ row, nameIndex }) {
  const [open, setOpen] = useState(false);
  const name = nameIndex[String(row.applicantId)]?.name || `Applicant ${row.applicantId}`;
  const missingCount = row.missing.length;
  const complete = missingCount === 0;

  return (
    <div style={{ borderBottom: `1px solid ${C.border}` }}>
      <div
        onClick={() => missingCount > 0 && setOpen(o => !o)}
        style={{
          display: "grid",
          gridTemplateColumns: "24px 1.8fr 0.7fr 1fr 0.8fr 0.7fr",
          gap: 12, padding: "10px 14px", alignItems: "center",
          fontFamily: FONT_DATA, fontSize: 12,
          cursor: missingCount > 0 ? "pointer" : "default",
          background: open ? C.bg : "transparent",
        }}
      >
        <div style={{ color: C.muted, fontSize: 11 }}>
          {missingCount > 0 ? (open ? "▾" : "▸") : ""}
        </div>
        <div>
          <div style={{ fontWeight: 600, color: C.ink }}>{name}</div>
          <div style={{ fontSize: 10, color: C.muted }}>
            Applicant {row.applicantId} · Application {row.applicationId}
          </div>
        </div>
        <div style={{ color: C.ink }}>{row.gradeApplyingFor ?? "—"}</div>
        <div style={{ color: C.ink }}>{row.statusDescription || row.status || "—"}</div>
        <div style={{ color: C.muted }}>{row.totalRequired} required</div>
        <div>
          <span style={{
            fontFamily: FONT_DATA, fontWeight: 700, fontSize: 12,
            padding: "2px 10px", borderRadius: 10,
            background: complete ? "#e6f4ea" : "#fbeaea",
            color: complete ? C.green : C.red,
            border: `1px solid ${complete ? C.green : C.red}`,
          }}>
            {complete ? "Complete" : `${missingCount} missing`}
          </span>
        </div>
      </div>

      {open && missingCount > 0 && (
        <div style={{ padding: "4px 14px 14px 50px", background: C.bg }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: FONT_DATA, fontSize: 11 }}>
            <thead>
              <tr style={{ color: C.muted, textAlign: "left" }}>
                <th style={{ padding: "4px 8px", fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", fontSize: 9 }}>Missing Item</th>
                <th style={{ padding: "4px 8px", fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", fontSize: 9 }}>Category</th>
                <th style={{ padding: "4px 8px", fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", fontSize: 9 }}>Scheduled</th>
              </tr>
            </thead>
            <tbody>
              {row.missing.map((m, i) => (
                <tr key={i} style={{ borderTop: `1px solid ${C.border}` }}>
                  <td style={{ padding: "5px 8px", color: C.ink, fontWeight: 600 }}>{m.description}</td>
                  <td style={{ padding: "5px 8px", color: C.muted }}>{m.category || "—"}</td>
                  <td style={{ padding: "5px 8px", color: C.muted }}>{m.scheduledDate || "Not scheduled"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const QUICK_ACTIONS = [
  { label: "All Applications (current filters)", path: "/admission/applications", params: {} },
  { label: "Relatives / Guardians",              path: "/admission/relatives",    params: {} },
];

const EXAMPLE_QUERIES = [
  "How many applications by grade level?",
  "Show application status breakdown for this year",
  "How many applications were submitted by month?",
  "Which candidate pools have the most pending applications?",
  "How many applicants are requesting financial aid?",
];

const CURRENT_YEAR = new Date().getFullYear() + 1; // admissions year applying for

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function AdmissionsExplorer() {
  // ── Top-level tab ──────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState("query"); // "query" | "checklist"

  // ── Shared connection state ────────────────────────────────────────────────
  const [connected,    setConnected]    = useState(false);
  const [applications, setApplications] = useState([]); // SPINE — scrubbed application records
  const [nameIndex,    setNameIndex]    = useState({}); // applicant_id -> { name, ... }

  // ── AI Query tab: filters ──────────────────────────────────────────────────
  const [filterYear,   setFilterYear]   = useState(String(CURRENT_YEAR));
  const [filterStatus, setFilterStatus] = useState("");
  const [filterPool,   setFilterPool]   = useState("");
  const [filterGrade,  setFilterGrade]  = useState("");
  const [searchText,   setSearchText]   = useState("");

  // ── AI Query tab: query + results ──────────────────────────────────────────
  const [query,        setQuery]        = useState("");
  const [results,      setResults]      = useState([]);
  const [history,      setHistory]      = useState([]);
  const [fetching,     setFetching]     = useState(false);
  const [pdfLoading,   setPdfLoading]   = useState(false);
  const [includeCharts, setIncludeCharts] = useState(false);
  const [error,        setError]        = useState("");
  const [log,          setLog]          = useState([]);
  const [showThread,   setShowThread]   = useState(false);

  // ── @ mention ──────────────────────────────────────────────────────────────
  const [mentionOpen,    setMentionOpen]    = useState(false);
  const [mentionResults, setMentionResults] = useState([]);
  const [mentionPos,     setMentionPos]     = useState({ top: 0, left: 0 });
  const [mentionTriggerIdx, setMentionTriggerIdx] = useState(-1);

  // ── Disambiguation ─────────────────────────────────────────────────────────
  const [disambig,     setDisambig]     = useState(null);
  const [pendingQuery, setPendingQuery] = useState("");

  // ── Checklist Tracker tab state ────────────────────────────────────────────
  const [clYearsInput,   setClYearsInput]   = useState(String(CURRENT_YEAR));
  const [clMode,         setClMode]         = useState("all"); // "all" | "single"
  const [clApplicantSel, setClApplicantSel] = useState("");
  const [clScanning,     setClScanning]     = useState(false);
  const [clProgress,     setClProgress]     = useState({ done: 0, total: 0 });
  const [clResults,      setClResults]      = useState(null);
  const [clSortKey,      setClSortKey]      = useState("missing");
  const [clSortDir,      setClSortDir]      = useState("desc");
  const [clShowComplete, setClShowComplete] = useState(true);
  const [clWarning,      setClWarning]      = useState(null);
  const [clError,        setClError]        = useState("");

  const cardRefs    = useRef([]);
  const logRef      = useRef(null);
  const textareaRef = useRef(null);

  const addLog = (msg, type = "info") => {
    setLog(prev => [...prev, { msg, type, ts: new Date().toLocaleTimeString() }]);
    setTimeout(() => logRef.current?.scrollTo(0, 99999), 50);
  };

  // ── Helper: load applications for a filter set, then resolve their names ───
  // Returns { apps (scrubbed), names (id->record) }.
  const loadApplicationsWithNames = async (params, label = "applications") => {
    addLog(`Loading ${label}…`);
    const rawApps = await vcFetchAllRaw("/admission/applications", params);
    const apps = scrubPII(rawApps);
    addLog(`  → ${apps.length} ${label} loaded`, "success");

    // Collect distinct applicant_ids that we don't already have names for.
    const needed = new Set();
    for (const a of rawApps) {
      const id = String(a.applicant_id || "");
      if (id && !nameIndex[id]) needed.add(id);
    }
    let names = {};
    if (needed.size > 0) {
      addLog(`Resolving ${needed.size} applicant name(s) — streaming applicant list…`);
      names = await resolveNamesForIds(needed, ({ found, total, page }) => {
        addLog(`  → names ${found}/${total} (page ${page})`, "info");
      });
      const missing = needed.size - Object.keys(names).length;
      addLog(missing > 0
        ? `  → resolved ${Object.keys(names).length}/${needed.size} names (${missing} not found in applicant list)`
        : `  → all ${needed.size} names resolved`, "success");
    }
    return { apps, names };
  };

  // ── Connect ────────────────────────────────────────────────────────────────
  const handleConnect = async () => {
    setError(""); setFetching(true);
    addLog("Connecting to Veracross…");
    try {
      const params = filterYear ? { year_applying_for: filterYear } : {};
      const { apps, names } = await loadApplicationsWithNames(params, "applications");
      setApplications(apps);
      setNameIndex(prev => ({ ...prev, ...names }));
      setConnected(true);
      addLog(`Connected — ${apps.length} applications · ${Object.keys(names).length} names indexed.`, "success");
    } catch (e) {
      setError(e.message); addLog(e.message, "error");
    } finally { setFetching(false); }
  };

  const refreshApplications = async () => {
    setFetching(true); setError("");
    try {
      const params = {};
      if (filterYear)   params.year_applying_for   = filterYear;
      if (filterStatus) params.application_status  = filterStatus;
      if (filterPool)   params.candidate_pool      = filterPool;
      if (filterGrade)  params.grade_applying_for  = filterGrade;
      const { apps, names } = await loadApplicationsWithNames(params, "applications");
      setApplications(apps);
      setNameIndex(prev => ({ ...prev, ...names }));
      addLog(`Refreshed — ${apps.length} applications match filters.`, "success");
    } catch (e) {
      setError(e.message); addLog(e.message, "error");
    } finally { setFetching(false); }
  };

  const filteredApplications = useMemo(() => {
    if (!searchText.trim()) return applications;
    const q = searchText.toLowerCase().trim();
    return applications.filter(a => {
      const id = a.applicant_id;
      const name = (nameIndex[String(id)]?.name || "").toLowerCase();
      if (name.includes(q)) return true;
      if (String(id).includes(q)) return true;
      if (String(a.application_id).includes(q)) return true;
      const status = (a.application_status_description || "").toLowerCase();
      if (status.includes(q)) return true;
      return false;
    });
  }, [applications, searchText, nameIndex]);

  // ── @ mention ──────────────────────────────────────────────────────────────
  const handleQueryChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    const cursor = e.target.selectionStart;
    const textBefore = val.slice(0, cursor);
    const atMatch = textBefore.match(/@(\w*)$/);
    if (atMatch) {
      const search = atMatch[1].toLowerCase();
      setMentionTriggerIdx(cursor - atMatch[0].length);
      const filtered = Object.entries(nameIndex)
        .filter(([, p]) => p.name.toLowerCase().includes(search))
        .slice(0, 8)
        .map(([id, p]) => ({ id, ...p }));
      setMentionResults(filtered);
      setMentionOpen(filtered.length > 0);
      if (textareaRef.current) {
        const rect = textareaRef.current.getBoundingClientRect();
        setMentionPos({ top: rect.bottom + window.scrollY + 4, left: rect.left + window.scrollX });
      }
    } else {
      setMentionOpen(false);
    }
  };
  const handleMentionSelect = (person) => {
    const before = query.slice(0, mentionTriggerIdx);
    const after  = query.slice(textareaRef.current?.selectionStart || query.length);
    setQuery(`${before}${person.name}${after}`);
    setMentionOpen(false);
    textareaRef.current?.focus();
  };

  // ── Submit query (AI Query tab) ────────────────────────────────────────────
  const handleQuery = useCallback(async (override, resolvedSelections) => {
    const q = (override || query).trim();
    if (!q || !connected) return;

    if (!resolvedSelections) {
      const detections = detectNames(q, nameIndex).filter(d => d.matches[0]?.score < 0.95);
      const exactMatches = detectNames(q, nameIndex).filter(d => d.matches[0]?.score >= 0.95);
      if (detections.length > 0) {
        setPendingQuery(q); setDisambig(detections); return;
      }
      if (exactMatches.length > 0) {
        const auto = {};
        exactMatches.forEach(d => { auto[d.original] = d.matches[0].id; });
        return handleQuery(q, auto);
      }
    }

    setError(""); setFetching(true);
    setQuery(""); setDisambig(null); setPendingQuery("");

    const resolutions = {};
    if (resolvedSelections) {
      for (const [original, id] of Object.entries(resolvedSelections)) {
        if (id !== "__none__" && nameIndex[id]) resolutions[original] = id;
      }
    }
    const dehydratedQuery = dehydrateQuery(q, resolutions);
    addLog(`Planning: "${dehydratedQuery}"`);

    const prevHistory = history;
    setHistory(prev => [...prev, { role: "user", content: dehydratedQuery }]);

    try {
      const filters = {
        year_applying_for:   filterYear,
        application_status:  filterStatus,
        candidate_pool:      filterPool,
        grade_applying_for:  filterGrade,
      };
      const plan = await planVCQuery(dehydratedQuery, filters);
      addLog(`Plan ready — ${plan.endpoints?.length || 0} endpoint(s)`);

      const allData = {};
      for (const ep of (plan.endpoints || [])) {
        let cleanPath = ep.path.replace(/^\/api\/v3/, "").replace(/^\/v3/, "");
        // Safety net: never let year_applying_for go to /admission/applicants.
        const params = { ...(ep.params || {}) };
        if (cleanPath === "/admission/applicants" && "year_applying_for" in params) {
          delete params.year_applying_for;
        }
        addLog(`Fetching ${cleanPath}…`);
        const { data: result } = await vcFetchAll(cleanPath, params);
        allData[ep.label || cleanPath] = result;
        addLog(`  → ${Array.isArray(result) ? result.length : 1} record(s)`, "success");
      }

      addLog("Analyzing with LLM…");
      const { narrative: analysis, charts } = await analyzeVCData(dehydratedQuery, allData, prevHistory, includeCharts);
      const rehydratedAnalysis = rehydrateText(analysis, nameIndex);

      const entry = { query: q, data: allData, analysis, rehydratedAnalysis, charts };
      setResults(prev => [...prev, entry]);
      setHistory(prev => [...prev, { role: "assistant", content: analysis }]);
      addLog("Done.", "success");
    } catch (e) {
      setError(e.message); addLog(e.message, "error");
      setHistory(prevHistory);
    } finally { setFetching(false); }
  }, [query, connected, history, nameIndex, filterYear, filterStatus, filterPool, filterGrade, includeCharts]);

  // ── Quick fetch (AI Query tab) ─────────────────────────────────────────────
  const handleQuickFetch = async (action) => {
    setFetching(true); setError("");
    addLog(`Quick fetch: ${action.label}`);
    try {
      const params = { ...action.params };
      // Filters only apply to the applications endpoint.
      if (action.path === "/admission/applications") {
        if (filterYear)   params.year_applying_for   = filterYear;
        if (filterStatus) params.application_status  = filterStatus;
        if (filterPool)   params.candidate_pool      = filterPool;
        if (filterGrade)  params.grade_applying_for  = filterGrade;
      }
      const { data } = await vcFetchAll(action.path, params);
      addLog(`  → ${data.length} record(s)`, "success");
      addLog("Analyzing…");

      const q = `Quick fetch: ${action.label}`;
      const allData = { [action.label]: data };
      const { narrative: analysis, charts } = await analyzeVCData(q, allData, history, includeCharts);
      const rehydratedAnalysis = rehydrateText(analysis, nameIndex);
      const entry = { query: q, data: allData, analysis, rehydratedAnalysis, charts };
      setResults(prev => [...prev, entry]);
      setHistory(prev => [...prev, { role: "user", content: q }, { role: "assistant", content: analysis }]);
      addLog("Done.", "success");
    } catch (e) {
      setError(e.message); addLog(e.message, "error");
    } finally { setFetching(false); }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // CHECKLIST TRACKER
  // ─────────────────────────────────────────────────────────────────────────

  const parseYears = (input) => {
    return [...new Set(
      String(input).split(/[,\s]+/).map(s => s.trim()).filter(s => /^\d{4}$/.test(s))
    )].sort();
  };

  // definitions: { checklist_item_id -> { description, category, ... } }
  const fetchDefinitions = async (year) => {
    const defs = await vcFetchOne(`/admission/config/years/${year}/checklists`);
    const map = {};
    for (const d of defs) {
      map[String(d.id)] = {
        description: d.description || d.abbreviation || `Item ${d.id}`,
        category: d.category?.description || null,
        categorySort: d.category?.sort_key ?? 999,
        sortKey: d.sort_key ?? 999,
        requiredForReview: !!d.required_for_review,
        requiredForApplication: !!d.required_for_application,
      };
    }
    return map;
  };

  // Core scan: for each {applicationId, ...}, fetch its checklist progress and
  // compute missing required items. Batched (5 at a time).
  const runChecklistScan = async (apps, definitionsByYear) => {
    const rows = [];
    const batchSize = 5;
    setClProgress({ done: 0, total: apps.length });

    for (let i = 0; i < apps.length; i += batchSize) {
      const batch = apps.slice(i, i + batchSize);
      const batchResults = await Promise.all(batch.map(async (app) => {
        try {
          const progressRecords = await vcFetchOne(
            `/admission/applications/${app.applicationId}/checklists`
          );
          const defs = definitionsByYear[String(app.yearApplyingFor)] || {};

          const missing = progressRecords
            .filter(p => p.required === true && p.completed === false)
            .map(p => {
              const def = defs[String(p.checklist_item_id)] || {};
              return {
                checklistItemId: p.checklist_item_id,
                description: def.description || `Item ${p.checklist_item_id}`,
                category: def.category || null,
                categorySort: def.categorySort ?? 999,
                sortKey: def.sortKey ?? 999,
                scheduledDate: p.scheduled_date || null,
              };
            })
            .sort((a, b) => a.categorySort - b.categorySort || a.sortKey - b.sortKey);

          const totalRequired = progressRecords.filter(p => p.required === true).length;

          return {
            applicantId: app.applicantId,
            applicationId: app.applicationId,
            yearApplyingFor: app.yearApplyingFor,
            gradeApplyingFor: app.gradeApplyingFor,
            status: app.status,
            statusDescription: app.statusDescription,
            totalRequired,
            completedRequired: totalRequired - missing.length,
            missing,
            error: null,
          };
        } catch (e) {
          return {
            applicantId: app.applicantId,
            applicationId: app.applicationId,
            yearApplyingFor: app.yearApplyingFor,
            gradeApplyingFor: app.gradeApplyingFor,
            status: app.status,
            statusDescription: app.statusDescription,
            totalRequired: 0, completedRequired: 0, missing: [],
            error: e.message,
          };
        }
      }));
      rows.push(...batchResults);
      setClProgress({ done: Math.min(i + batchSize, apps.length), total: apps.length });
      addLog(`Checklist scan — ${Math.min(i + batchSize, apps.length)}/${apps.length} applications`, "info");
    }
    return rows;
  };

  // Gather applications for selected years, then scan.
  const executeChecklistScan = async (years) => {
    setClScanning(true);
    setClError("");
    setClResults(null);
    addLog(`Checklist scan starting — years: ${years.join(", ")}`);

    try {
      // 1. Definitions per year.
      const definitionsByYear = {};
      for (const y of years) {
        addLog(`Loading checklist definitions for ${y}…`);
        definitionsByYear[y] = await fetchDefinitions(y);
      }

      // 2. Gather applications.
      let apps = [];
      const rawAppsAll = [];
      for (const y of years) {
        addLog(`Loading applications for ${y}…`);
        const rawApps = await vcFetchAllRaw("/admission/applications", { year_applying_for: y });
        rawAppsAll.push(...rawApps);
        rawApps.forEach(a => apps.push({
          applicationId: a.application_id,
          applicantId: a.applicant_id,
          yearApplyingFor: a.year_applying_for,
          gradeApplyingFor: a.grade_applying_for,
          status: a.application_status,
          statusDescription: a.application_status_description,
        }));
      }

      if (clMode === "single") {
        if (!clApplicantSel) throw new Error("Select an applicant for single-applicant mode.");
        apps = apps.filter(a => String(a.applicantId) === String(clApplicantSel));
        if (apps.length === 0) {
          throw new Error("No applications found for that applicant in the selected year(s).");
        }
      }

      addLog(`Scanning checklists for ${apps.length} application(s)…`);

      // 3. Scan.
      const rows = await runChecklistScan(apps, definitionsByYear);

      // 4. Resolve any missing names (set-driven early-exit).
      const needed = new Set(
        rows.map(r => String(r.applicantId)).filter(id => id && !nameIndex[id])
      );
      if (needed.size > 0) {
        addLog(`Resolving ${needed.size} applicant name(s)…`);
        const names = await resolveNamesForIds(needed, ({ found, total, page }) => {
          addLog(`  → names ${found}/${total} (page ${page})`, "info");
        });
        setNameIndex(prev => ({ ...prev, ...names }));
      }

      setClResults({
        rows,
        years,
        scannedAt: new Date(),
        errorCount: rows.filter(r => r.error).length,
      });
      addLog(`Checklist scan complete — ${rows.length} applications scanned.`, "success");
    } catch (e) {
      setClError(e.message);
      addLog(`Checklist scan failed: ${e.message}`, "error");
    } finally {
      setClScanning(false);
    }
  };

  // Entry point — validates years, shows warning for multi/non-current year.
  const handleChecklistScan = async () => {
    setClError("");
    const years = parseYears(clYearsInput);
    if (years.length === 0) {
      setClError("Enter at least one valid 4-digit year (e.g. 2027, or 2026 2027).");
      return;
    }
    const isOnlyCurrent = years.length === 1 && years[0] === String(CURRENT_YEAR);

    if (!isOnlyCurrent) {
      // vcCount may return null (Veracross omits the count header). Only sum
      // real numbers; if none come back, leave the estimate as null/unknown.
      let estimate = null;
      try {
        for (const y of years) {
          const n = await vcCount("/admission/applications", { year_applying_for: y });
          if (typeof n === "number") estimate = (estimate || 0) + n;
        }
      } catch { estimate = null; }
      setClWarning({ years, estimatedCount: estimate });
      return;
    }
    executeChecklistScan(years);
  };

  // ── Checklist analytics (computed, no LLM) ─────────────────────────────────
  const checklistAnalytics = useMemo(() => {
    if (!clResults) return null;
    const rows = clResults.rows;
    const total = rows.length;
    const withMissing = rows.filter(r => r.missing.length > 0).length;
    const complete = total - withMissing;

    const totalRequired = rows.reduce((s, r) => s + r.totalRequired, 0);
    const totalCompleted = rows.reduce((s, r) => s + r.completedRequired, 0);
    const completionRate = totalRequired ? (totalCompleted / totalRequired) * 100 : 100;

    const itemCounts = {};
    for (const r of rows) {
      for (const m of r.missing) {
        itemCounts[m.description] = (itemCounts[m.description] || 0) + 1;
      }
    }
    const topMissing = Object.entries(itemCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([desc, count]) => ({ desc, count }));

    return { total, withMissing, complete, completionRate, topMissing };
  }, [clResults]);

  // ── Sorted + filtered checklist rows ───────────────────────────────────────
  const checklistTableRows = useMemo(() => {
    if (!clResults) return [];
    let rows = [...clResults.rows];
    if (!clShowComplete) rows = rows.filter(r => r.missing.length > 0);

    const dir = clSortDir === "asc" ? 1 : -1;
    rows.sort((a, b) => {
      let av, bv;
      switch (clSortKey) {
        case "name":
          av = (nameIndex[String(a.applicantId)]?.name || "").toLowerCase();
          bv = (nameIndex[String(b.applicantId)]?.name || "").toLowerCase();
          return av < bv ? -dir : av > bv ? dir : 0;
        case "grade":
          av = a.gradeApplyingFor ?? -1; bv = b.gradeApplyingFor ?? -1;
          return (av - bv) * dir;
        case "status":
          av = (a.statusDescription || String(a.status) || "").toLowerCase();
          bv = (b.statusDescription || String(b.status) || "").toLowerCase();
          return av < bv ? -dir : av > bv ? dir : 0;
        case "missing":
        default:
          return (a.missing.length - b.missing.length) * dir;
      }
    });
    return rows;
  }, [clResults, clSortKey, clSortDir, clShowComplete, nameIndex]);

  const toggleClSort = (key) => {
    if (clSortKey === key) setClSortDir(d => (d === "asc" ? "desc" : "asc"));
    else { setClSortKey(key); setClSortDir(key === "name" ? "asc" : "desc"); }
  };

  // ── PDF exports (AI Query tab) ─────────────────────────────────────────────
  const handleDownloadSingle = useCallback(async (idx) => {
    const entry = results[idx];
    const cardNode = cardRefs.current[idx];
    if (!entry) return;
    setPdfLoading(true);
    try {
      const JSPDF = await getJSPDF();
      const doc = new JSPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const W = doc.internal.pageSize.getWidth();
      const H = doc.internal.pageSize.getHeight();
      await buildQueryPage(doc, W, H, entry, idx, results.length, cardNode);
      addFooters(doc, W, H);
      const safe = entry.query.replace(/\s+/g, "_").replace(/[^a-zA-Z0-9_]/g, "").slice(0, 40);
      doc.save(`KA_Admissions_${safe}.pdf`);
    } catch (e) { setError("PDF failed: " + e.message); }
    finally { setPdfLoading(false); }
  }, [results]);

  const handleDownloadAll = useCallback(async () => {
    if (!results.length) return;
    setPdfLoading(true);
    try {
      const JSPDF = await getJSPDF();
      const doc = new JSPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const W = doc.internal.pageSize.getWidth();
      const H = doc.internal.pageSize.getHeight();
      const now = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

      doc.setFillColor(...NAVY_P); doc.rect(0, 0, W, H, "F");
      doc.setFillColor(...GOLD_P); doc.rect(0, 0, 2, H, "F");
      doc.setFillColor(...GOLD_P); doc.rect(W - 2, 0, 2, H, "F");
      doc.setTextColor(212, 172, 106); doc.setFontSize(10); doc.setFont("helvetica", "bold");
      doc.text("KING'S ACADEMY", W / 2, 60, { align: "center", charSpace: 5 });
      doc.setFontSize(7); doc.setFont("helvetica", "normal"); doc.setTextColor(140, 120, 90);
      doc.text("EST. 1999  ·  MADABA, JORDAN", W / 2, 70, { align: "center", charSpace: 1 });
      doc.setDrawColor(...GOLD_P); doc.setLineWidth(0.4); doc.line(W / 2 - 22, 75, W / 2 + 22, 75);
      doc.setTextColor(250, 247, 242); doc.setFontSize(30); doc.setFont("helvetica", "bold");
      doc.text("Admissions Intelligence", W / 2, 110, { align: "center" });
      doc.setFontSize(13); doc.setFont("helvetica", "italic"); doc.setTextColor(180, 170, 155);
      doc.text("Veracross Pipeline Report", W / 2, 123, { align: "center" });
      doc.setDrawColor(...GOLD_P); doc.setLineWidth(0.6); doc.line(16, 132, W - 16, 132);
      doc.setLineWidth(0.2); doc.line(16, 135, W - 16, 135);

      doc.setFillColor(13, 45, 14);
      doc.roundedRect(W / 2 - 62, 168, 124, 12, 1, 1, "F");
      doc.setDrawColor(34, 134, 58);
      doc.roundedRect(W / 2 - 62, 168, 124, 12, 1, 1, "S");
      doc.setTextColor(63, 185, 80); doc.setFontSize(7); doc.setFont("helvetica", "bold");
      doc.text("PII-FREE  —  No applicant names, emails, phones, or DOB in this report", W / 2, 175.5, { align: "center" });
      doc.setFontSize(9); doc.setFont("helvetica", "normal"); doc.setTextColor(120, 110, 95);
      doc.text(`${results.length} quer${results.length > 1 ? "ies" : "y"}  ·  ${now}`, W / 2, 190, { align: "center" });

      doc.setFillColor(12, 20, 35); doc.rect(0, H - 14, W, 14, "F");
      doc.setTextColor(80, 70, 60); doc.setFontSize(7); doc.setFont("helvetica", "italic");
      doc.text("Internal use only · King's Academy Admissions Intelligence · Confidential", W / 2, H - 5, { align: "center" });

      for (let i = 0; i < results.length; i++) {
        doc.addPage();
        await buildQueryPage(doc, W, H, results[i], i, results.length, cardRefs.current[i]);
      }
      addFooters(doc, W, H);
      doc.save(`KA_Admissions_Report_${now.replace(/,?\s+/g, "_")}.pdf`);
    } catch (e) { setError("Report failed: " + e.message); }
    finally { setPdfLoading(false); }
  }, [results]);

  // ─── STYLES ──────────────────────────────────────────────────────────────
  const S = {
    wrap:       { fontFamily: FONT_BODY, background: C.cream, minHeight: "100vh", color: C.ink },
    header:     { background: C.ink, padding: "2rem 2rem 0", borderBottom: `3px solid ${C.gold}` },
    inner:      { maxWidth: 1100, margin: "0 auto" },
    main:       { maxWidth: 1100, margin: "0 auto", padding: "2rem", display: "grid", gridTemplateColumns: "280px 1fr", gap: "1.5rem", alignItems: "start" },
    sidebar:    { position: "sticky", top: "1rem" },
    card:       { background: "#fff", border: `1px solid ${C.border}`, borderRadius: 3, padding: "1.2rem", marginBottom: "1rem", boxShadow: "0 1px 6px rgba(0,0,0,0.05)" },
    label:      { display: "block", fontFamily: FONT_DATA, fontSize: "0.7rem", fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: C.muted, marginBottom: 4 },
    input:      { width: "100%", background: C.cream, border: `1px solid ${C.border}`, borderRadius: 2, padding: "8px 10px", color: C.ink, fontSize: "0.88rem", fontFamily: FONT_BODY, boxSizing: "border-box", marginBottom: 10 },
    btnPrimary: { padding: "7px 16px", borderRadius: 2, border: "none", cursor: "pointer", fontFamily: FONT_DATA, fontSize: "0.82rem", fontWeight: 600, background: C.navy, color: C.cream },
    btnGold:    { padding: "10px 22px", borderRadius: 2, border: "none", cursor: "pointer", fontFamily: FONT_DATA, fontSize: "0.9rem", fontWeight: 600, background: C.gold, color: C.ink },
    btnGhost:   { padding: "7px 12px", borderRadius: 2, border: `1px solid ${C.border}`, cursor: "pointer", fontFamily: FONT_DATA, fontSize: "0.8rem", fontWeight: 500, background: C.bg, color: C.muted, textAlign: "left", width: "100%" },
    piiNotice:  { background: "#0d2d0e", border: "1px solid #238636", borderRadius: 3, padding: "8px 14px", fontFamily: FONT_DATA, fontSize: "0.75rem", color: "#3fb950", marginBottom: "1rem", display: "flex", gap: 8, alignItems: "center" },
    logBox:     { background: "#f8f6f2", border: `1px solid ${C.border}`, borderRadius: 2, padding: 10, maxHeight: 130, overflowY: "auto", fontSize: "0.72rem", fontFamily: FONT_DATA, lineHeight: 1.8 },
    errorBox:   { background: "#fff5f5", border: `1px solid ${C.red}`, borderRadius: 2, padding: "10px 14px", fontFamily: FONT_DATA, fontSize: 13, color: C.red, marginBottom: 16 },
    pill:       { fontFamily: FONT_DATA, fontSize: 11, padding: "4px 11px", border: `1px solid ${C.border}`, borderRadius: 10, background: C.bg, color: C.muted, cursor: "pointer" },
    tab:        (active) => ({ fontFamily: FONT_DATA, fontSize: 12, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", padding: "12px 22px", border: "none", background: "transparent", color: active ? C.gold : "rgba(250,247,242,0.55)", borderBottom: active ? `3px solid ${C.gold}` : "3px solid transparent", cursor: "pointer", marginBottom: -3 }),
    statBox:    { background: "#fff", border: `1px solid ${C.border}`, borderRadius: 3, padding: "14px 16px", textAlign: "center" },
  };

  const hasResults = results.length > 0;

  return (
    <div style={S.wrap}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,600;0,700;1,400&family=EB+Garamond:ital,wght@0,400;0,500;1,400&family=Rajdhani:wght@400;500;600&display=swap');
        * { box-sizing: border-box; }
        button { cursor: pointer; }
        select:focus, input:focus, textarea:focus { outline: 2px solid ${C.navy}; outline-offset: 1px; }
        .mention-item:hover { background: ${C.bg} !important; }
      `}</style>

      {disambig && (
        <DisambiguationDialog
          detections={disambig} nameIndex={nameIndex}
          onConfirm={(selections) => handleQuery(pendingQuery, selections)}
          onCancel={() => { setDisambig(null); setPendingQuery(""); }}
        />
      )}

      {clWarning && (
        <ScanWarningDialog
          years={clWarning.years}
          estimatedCount={clWarning.estimatedCount}
          onConfirm={() => { const w = clWarning; setClWarning(null); executeChecklistScan(w.years); }}
          onCancel={() => setClWarning(null)}
        />
      )}

      {mentionOpen && (
        <div style={{ position: "absolute", top: mentionPos.top, left: mentionPos.left, zIndex: 500, background: "#fff", border: `1px solid ${C.border}`, borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.12)", minWidth: 260, maxHeight: 260, overflowY: "auto" }}>
          <div style={{ padding: "6px 12px 4px", fontFamily: FONT_DATA, fontSize: 9, fontWeight: 600, letterSpacing: "0.15em", textTransform: "uppercase", color: C.muted, borderBottom: `1px solid ${C.border}` }}>
            Applicants
          </div>
          {mentionResults.map(person => (
            <div key={person.id} className="mention-item"
              onClick={() => handleMentionSelect(person)}
              style={{ padding: "8px 12px", cursor: "pointer", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontFamily: FONT_DATA, fontSize: 13, fontWeight: 600, color: C.ink }}>{person.name}</div>
                <div style={{ fontFamily: FONT_DATA, fontSize: 10, color: C.muted }}>
                  Applicant{person.grade ? ` · ${person.grade}` : ""}
                </div>
              </div>
              <div style={{ fontFamily: FONT_DATA, fontSize: 10, color: C.muted }}>ID: {person.id}</div>
            </div>
          ))}
        </div>
      )}

      {/* Header + tabs */}
      <header style={S.header}>
        <div style={{ ...S.inner, display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 16, paddingBottom: "1.4rem" }}>
          <div>
            <div style={{ fontFamily: FONT_DATA, fontSize: 10, fontWeight: 600, letterSpacing: "0.25em", color: C.gold, textTransform: "uppercase", marginBottom: 8 }}>
              King's Academy · Admissions
            </div>
            <h1 style={{ fontFamily: FONT_DISPLAY, fontSize: "clamp(1.4rem,3vw,2rem)", fontWeight: 700, color: C.cream, margin: "0 0 6px", lineHeight: 1.15 }}>
              Applicant <em style={{ color: C.gold }}>Intelligence</em>
            </h1>
            <p style={{ fontFamily: FONT_BODY, fontStyle: "italic", color: "rgba(250,247,242,0.5)", fontSize: 14, margin: 0 }}>
              Veracross search · PII-free AI analysis · checklist tracking
            </p>
          </div>
          {activeTab === "query" && hasResults && (
            <button onClick={handleDownloadAll} disabled={pdfLoading} style={{ ...S.btnGold, flexShrink: 0, opacity: pdfLoading ? 0.7 : 1 }}>
              {pdfLoading ? "Building…" : `↓ Full Report (${results.length})`}
            </button>
          )}
        </div>
        <div style={{ ...S.inner, display: "flex", gap: 4 }}>
          <button style={S.tab(activeTab === "query")} onClick={() => setActiveTab("query")}>AI Query</button>
          <button style={S.tab(activeTab === "checklist")} onClick={() => setActiveTab("checklist")}>Checklist Tracker</button>
        </div>
      </header>

      <main style={S.main}>
        {/* ─────────────────── SIDEBAR ─────────────────── */}
        <div style={S.sidebar}>
          {!connected ? (
            <div style={S.card}>
              <div style={{ fontFamily: FONT_DATA, fontSize: 9, fontWeight: 600, letterSpacing: "0.15em", textTransform: "uppercase", color: C.muted, marginBottom: 12 }}>Veracross Connection</div>
              <label style={S.label}>School Route</label>
              <input style={S.input} value="kingsacademy" readOnly />
              <label style={S.label}>Token Source</label>
              <input style={S.input} value="Firestore · config/vc_admissions_token" readOnly />
              <label style={S.label}>Year Applying For</label>
              <input style={S.input} value={filterYear} onChange={e => setFilterYear(e.target.value)} placeholder={String(CURRENT_YEAR)} />
              <button style={{ ...S.btnPrimary, width: "100%", padding: "9px" }} onClick={handleConnect} disabled={fetching}>
                {fetching ? "Connecting…" : "Connect & Load Applications"}
              </button>
            </div>
          ) : (
            <div style={{ ...S.card, borderLeft: `3px solid ${C.gold}` }}>
              <div style={{ fontFamily: FONT_DATA, fontSize: 9, color: C.green, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 4 }}>● Connected</div>
              <div style={{ fontFamily: FONT_DATA, fontSize: 13, fontWeight: 600, color: C.navy }}>kingsacademy</div>
              <div style={{ fontFamily: FONT_DATA, fontSize: 11, color: C.muted, marginTop: 2 }}>
                {applications.length} applications · {Object.keys(nameIndex).length} names indexed
              </div>
            </div>
          )}

          {/* ── AI QUERY TAB SIDEBAR ── */}
          {activeTab === "query" && connected && (
            <>
              <div style={S.card}>
                <div style={{ fontFamily: FONT_DATA, fontSize: 9, fontWeight: 600, letterSpacing: "0.15em", textTransform: "uppercase", color: C.muted, marginBottom: 10 }}>Filters</div>
                <label style={S.label}>Year Applying For</label>
                <input style={S.input} value={filterYear} onChange={e => setFilterYear(e.target.value)} placeholder={`e.g. ${CURRENT_YEAR}`} />
                <label style={S.label}>Application Status (ID)</label>
                <input style={S.input} value={filterStatus} onChange={e => setFilterStatus(e.target.value)} placeholder="e.g. 10" />
                <label style={S.label}>Candidate Pool (ID)</label>
                <input style={S.input} value={filterPool} onChange={e => setFilterPool(e.target.value)} placeholder="e.g. 3" />
                <label style={S.label}>Grade Applying For</label>
                <input style={S.input} value={filterGrade} onChange={e => setFilterGrade(e.target.value)} placeholder="e.g. 9" />
                <button style={{ ...S.btnPrimary, width: "100%", padding: "8px" }} onClick={refreshApplications} disabled={fetching}>
                  Apply Filters
                </button>
                <div style={{ fontFamily: FONT_DATA, fontSize: 9, color: C.muted, marginTop: 8, fontStyle: "italic", lineHeight: 1.5 }}>
                  Filters apply to applications. VC uses enum IDs for status/pool — check the Raw Data tab on a result to find the IDs your school uses.
                </div>
              </div>

              <div style={S.card}>
                <div style={{ fontFamily: FONT_DATA, fontSize: 9, fontWeight: 600, letterSpacing: "0.15em", textTransform: "uppercase", color: C.muted, marginBottom: 10 }}>Quick Fetch + Analyze</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {QUICK_ACTIONS.map(a => (
                    <button key={a.label} style={S.btnGhost} onClick={() => handleQuickFetch(a)} disabled={fetching}>
                      {a.label}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* ── CHECKLIST TAB SIDEBAR ── */}
          {activeTab === "checklist" && connected && (
            <div style={S.card}>
              <div style={{ fontFamily: FONT_DATA, fontSize: 9, fontWeight: 600, letterSpacing: "0.15em", textTransform: "uppercase", color: C.muted, marginBottom: 10 }}>Checklist Scan</div>

              <label style={S.label}>Year(s) to Scan</label>
              <input
                style={S.input}
                value={clYearsInput}
                onChange={e => setClYearsInput(e.target.value)}
                placeholder={`e.g. ${CURRENT_YEAR}  or  ${CURRENT_YEAR - 1} ${CURRENT_YEAR}`}
              />
              <div style={{ fontFamily: FONT_DATA, fontSize: 9, color: C.muted, marginBottom: 10, fontStyle: "italic", lineHeight: 1.5 }}>
                One current year scans fast. Multiple years (or any past year) triggers a long-operation warning before running.
              </div>

              <label style={S.label}>Scan Mode</label>
              <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
                {[["all", "All applications"], ["single", "One applicant"]].map(([m, lbl]) => (
                  <button key={m} onClick={() => setClMode(m)}
                    style={{
                      flex: 1, padding: "7px 4px", borderRadius: 2,
                      border: `1px solid ${clMode === m ? C.navy : C.border}`,
                      background: clMode === m ? "#e8f0f8" : C.cream,
                      color: clMode === m ? C.navy : C.muted,
                      fontFamily: FONT_DATA, fontSize: 11, fontWeight: 600, cursor: "pointer",
                    }}>
                    {lbl}
                  </button>
                ))}
              </div>

              {clMode === "single" && (
                <>
                  <label style={S.label}>Applicant</label>
                  <select style={S.input} value={clApplicantSel} onChange={e => setClApplicantSel(e.target.value)}>
                    <option value="">— select an applicant —</option>
                    {Object.entries(nameIndex)
                      .sort((a, b) => a[1].name.localeCompare(b[1].name))
                      .map(([id, p]) => (
                        <option key={id} value={id}>{p.name} (ID {id})</option>
                      ))}
                  </select>
                  <div style={{ fontFamily: FONT_DATA, fontSize: 9, color: C.muted, marginBottom: 10, fontStyle: "italic" }}>
                    Only applicants already in the name index appear here. Load more by connecting / applying filters on the AI Query tab.
                  </div>
                </>
              )}

              <button
                style={{ ...S.btnPrimary, width: "100%", padding: "9px", background: clScanning ? C.muted : C.navy }}
                onClick={handleChecklistScan}
                disabled={clScanning}
              >
                {clScanning ? `Scanning… ${clProgress.done}/${clProgress.total}` : "Run Checklist Scan"}
              </button>

              {clScanning && clProgress.total > 0 && (
                <div style={{ marginTop: 10, height: 6, background: C.border, borderRadius: 3, overflow: "hidden" }}>
                  <div style={{ height: "100%", background: C.gold, width: `${(clProgress.done / clProgress.total) * 100}%`, transition: "width 0.3s" }} />
                </div>
              )}
            </div>
          )}

          {/* Activity log (shared) */}
          <div style={S.card}>
            <div style={{ fontFamily: FONT_DATA, fontSize: 9, fontWeight: 600, letterSpacing: "0.15em", textTransform: "uppercase", color: C.muted, marginBottom: 8 }}>Activity</div>
            <div style={S.logBox} ref={logRef}>
              {log.length === 0 && <span style={{ color: "#c8bfb0" }}>No activity yet.</span>}
              {log.map((l, i) => (
                <div key={i} style={{ color: l.type === "error" ? C.red : l.type === "success" ? C.green : C.muted }}>
                  <span style={{ color: "#c8bfb0" }}>{l.ts} </span>{l.msg}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ─────────────────── MAIN CONTENT ─────────────────── */}
        <div>
          {/* ════════ AI QUERY TAB ════════ */}
          {activeTab === "query" && (
            <>
              <div style={S.piiNotice}>
                <span>🔒</span>
                <span>Names, emails, phones, addresses, DOB stripped before LLM · rehydrated for display only · raw data anonymized · type @ to mention an applicant</span>
              </div>

              {error && <div style={S.errorBox}>⚠ {error}</div>}

              {connected && (
                <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 3, marginBottom: 24, overflow: "hidden", boxShadow: "0 1px 8px rgba(0,0,0,0.05)" }}>
                  <div style={{ padding: "12px 20px", borderBottom: `1px solid ${C.border}`, background: C.bg, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
                    <div style={{ fontFamily: FONT_DATA, fontSize: 10, fontWeight: 600, letterSpacing: "0.18em", textTransform: "uppercase", color: C.muted }}>
                      Browse Applications  —  {filteredApplications.length} of {applications.length}
                    </div>
                    <input
                      value={searchText}
                      onChange={e => setSearchText(e.target.value)}
                      placeholder="Search by name, ID, or status…"
                      style={{ ...S.input, marginBottom: 0, width: 280 }}
                    />
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr 0.6fr 0.6fr 1fr 0.9fr", gap: 12, padding: "8px 14px", borderBottom: `1px solid ${C.border}`, background: "#fdfcf8", fontFamily: FONT_DATA, fontSize: 9, fontWeight: 600, letterSpacing: "0.15em", textTransform: "uppercase", color: C.muted }}>
                    <div>Applicant</div><div>Status</div><div>Grade</div><div>Year</div><div>Pool</div><div>App Date</div>
                  </div>
                  <div style={{ maxHeight: 340, overflowY: "auto" }}>
                    {filteredApplications.length === 0 ? (
                      <div style={{ padding: 24, textAlign: "center", fontFamily: FONT_BODY, fontStyle: "italic", color: C.muted }}>
                        No applications match the current filters.
                      </div>
                    ) : (
                      filteredApplications.slice(0, 200).map(a => (
                        <ApplicationRow key={a.application_id} app={a} nameIndex={nameIndex} />
                      ))
                    )}
                    {filteredApplications.length > 200 && (
                      <div style={{ padding: 12, textAlign: "center", fontFamily: FONT_DATA, fontSize: 11, color: C.muted, fontStyle: "italic", background: C.bg }}>
                        … showing first 200. Refine search or filters to see more.
                      </div>
                    )}
                  </div>
                </div>
              )}

              {results.map((entry, idx) => (
                <ResultCard
                  key={idx} entry={entry} idx={idx} total={results.length}
                  onDownload={handleDownloadSingle} pdfLoading={pdfLoading}
                  cardRef={el => { cardRefs.current[idx] = el; }}
                />
              ))}

              {fetching && (
                <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 3, padding: 32, textAlign: "center", marginBottom: 24 }}>
                  <div style={{ fontFamily: FONT_DISPLAY, fontSize: "1.05rem", color: C.muted, fontStyle: "italic", marginBottom: 6 }}>Working…</div>
                  <div style={{ fontFamily: FONT_DATA, fontSize: 11, color: "#c8bfb0" }}>Fetching VC data · scrubbing PII · analyzing</div>
                </div>
              )}

              {connected && (
                <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 3, overflow: "hidden", boxShadow: "0 1px 8px rgba(0,0,0,0.05)" }}>
                  <div style={{ background: C.bg, borderBottom: `1px solid ${C.border}`, padding: "12px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                    <div style={{ fontFamily: FONT_DATA, fontSize: 10, fontWeight: 600, letterSpacing: "0.18em", textTransform: "uppercase", color: C.muted }}>
                      {hasResults ? "Ask a follow-up or new question" : "Ask about your admissions pipeline"}
                    </div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <button onClick={() => setIncludeCharts(v => !v)}
                        style={{ fontFamily: FONT_DATA, fontSize: 11, fontWeight: 600, padding: "5px 12px", border: `1px solid ${includeCharts ? C.gold : C.border}`, borderRadius: 2, background: includeCharts ? "#fff8ed" : C.cream, color: includeCharts ? C.gold : C.muted, display: "flex", alignItems: "center", gap: 5, cursor: "pointer" }}>
                        <span style={{ fontSize: 13 }}>📊</span>
                        {includeCharts ? "Charts On" : "Charts Off"}
                      </button>
                      {hasResults && (
                        <button onClick={() => setShowThread(s => !s)}
                          style={{ fontFamily: FONT_DATA, fontSize: 11, fontWeight: 600, padding: "5px 14px", border: `1px solid ${showThread ? C.navy : C.border}`, borderRadius: 2, background: showThread ? "#e8f0f8" : C.cream, color: showThread ? C.navy : C.muted, cursor: "pointer" }}>
                          {showThread ? "▲ Hide Thread" : "▼ View Thread"}
                        </button>
                      )}
                    </div>
                  </div>

                  {showThread && history.length > 0 && (
                    <div style={{ borderBottom: `1px solid ${C.border}`, maxHeight: 280, overflowY: "auto", padding: "14px 20px", background: "#fdfcf8", display: "flex", flexDirection: "column", gap: 10 }}>
                      {history.map((msg, i) => (
                        <div key={i} style={{ display: "flex", gap: 10 }}>
                          <div style={{ flexShrink: 0, width: 26, height: 26, borderRadius: "50%", background: msg.role === "user" ? C.navy : C.gold, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: FONT_DATA, fontSize: 9, fontWeight: 700, color: "#fff" }}>
                            {msg.role === "user" ? "Q" : "AI"}
                          </div>
                          <div style={{ fontFamily: FONT_BODY, fontSize: 13, color: C.ink, lineHeight: 1.6, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 2, padding: "8px 12px", flex: 1 }}>
                            {msg.content.slice(0, 320)}{msg.content.length > 320 ? "…" : ""}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div style={{ padding: "16px 20px" }}>
                    <textarea
                      ref={textareaRef}
                      value={query}
                      onChange={handleQueryChange}
                      onKeyDown={e => {
                        if (mentionOpen && e.key === "Escape") { setMentionOpen(false); return; }
                        if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleQuery();
                      }}
                      placeholder={hasResults
                        ? "Refine or ask something new… type @ to mention an applicant (⌘+Enter to send)"
                        : "e.g. How many applications were submitted by month? (⌘+Enter to send)"}
                      rows={2}
                      style={{ ...S.input, marginBottom: 10, resize: "none", lineHeight: 1.55 }}
                    />
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        {EXAMPLE_QUERIES.slice(0, 3).map(ex => (
                          <button key={ex} style={S.pill} onClick={() => handleQuery(ex)} disabled={fetching}>
                            {ex}
                          </button>
                        ))}
                      </div>
                      <button
                        style={{ ...S.btnPrimary, padding: "8px 22px", fontSize: 13, opacity: (!query.trim() || !connected) ? 0.5 : 1 }}
                        onClick={() => handleQuery()}
                        disabled={fetching || !query.trim() || !connected}
                      >
                        {fetching ? "Fetching…" : hasResults ? "Send →" : "Run Query →"}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {!hasResults && !fetching && connected && (
                <div style={{ marginTop: 16, padding: "16px 20px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 2, fontFamily: FONT_BODY, fontSize: 13, color: C.muted, lineHeight: 1.75 }}>
                  <strong style={{ fontFamily: FONT_DATA, fontSize: 9, letterSpacing: "0.15em", textTransform: "uppercase", display: "block", marginBottom: 6 }}>How it works</strong>
                  Browse applications above, use quick-fetch actions in the sidebar, or type a natural-language question. Applications are the data spine — filterable by year, status, pool, and grade. All names, contact info, and DOB are scrubbed before any data reaches the AI; only pseudonymized IDs are retained for joins. For checklist completion tracking, switch to the Checklist Tracker tab.
                </div>
              )}

              {!connected && (
                <div style={{ padding: "16px 20px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 2, fontFamily: FONT_BODY, fontSize: 13, color: C.muted, lineHeight: 1.75 }}>
                  <strong style={{ fontFamily: FONT_DATA, fontSize: 9, letterSpacing: "0.15em", textTransform: "uppercase", display: "block", marginBottom: 6 }}>Get started</strong>
                  Connect to Veracross using the panel on the left. The tool loads applications for the chosen year, then resolves applicant names by streaming the applicant list and stopping as soon as every needed name is found. The same connection serves both the AI Query and Checklist Tracker tabs.
                </div>
              )}
            </>
          )}

          {/* ════════ CHECKLIST TRACKER TAB ════════ */}
          {activeTab === "checklist" && (
            <>
              <div style={S.piiNotice}>
                <span>🔒</span>
                <span>Checklist scan runs entirely in-browser — no LLM. Applicant names shown are rehydrated locally. CSV export is labelled CONFIDENTIAL because it includes names as a worklist.</span>
              </div>

              {clError && <div style={S.errorBox}>⚠ {clError}</div>}

              {!connected && (
                <div style={{ padding: "16px 20px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 2, fontFamily: FONT_BODY, fontSize: 13, color: C.muted, lineHeight: 1.75 }}>
                  <strong style={{ fontFamily: FONT_DATA, fontSize: 9, letterSpacing: "0.15em", textTransform: "uppercase", display: "block", marginBottom: 6 }}>Connect first</strong>
                  Use the connection panel on the left to connect to Veracross, then configure and run a checklist scan.
                </div>
              )}

              {connected && !clResults && !clScanning && (
                <div style={{ padding: "16px 20px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 2, fontFamily: FONT_BODY, fontSize: 13, color: C.muted, lineHeight: 1.75 }}>
                  <strong style={{ fontFamily: FONT_DATA, fontSize: 9, letterSpacing: "0.15em", textTransform: "uppercase", display: "block", marginBottom: 6 }}>Checklist Tracker</strong>
                  Pick the year(s) to scan and a mode in the sidebar, then run the scan. The tool fetches every application's checklist progress and computes which required items are still outstanding. Results appear here as a sortable table with completion analytics. Scanning a single current year is fast; multiple or past years can take several minutes and will warn you first.
                </div>
              )}

              {clScanning && (
                <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 3, padding: 32, textAlign: "center", marginBottom: 24 }}>
                  <div style={{ fontFamily: FONT_DISPLAY, fontSize: "1.05rem", color: C.muted, fontStyle: "italic", marginBottom: 6 }}>
                    Scanning checklists… {clProgress.done}/{clProgress.total}
                  </div>
                  <div style={{ fontFamily: FONT_DATA, fontSize: 11, color: "#c8bfb0" }}>
                    Fetching per-application checklist progress in batches
                  </div>
                  <div style={{ marginTop: 14, height: 8, background: C.border, borderRadius: 4, overflow: "hidden", maxWidth: 360, margin: "14px auto 0" }}>
                    <div style={{ height: "100%", background: C.gold, width: `${clProgress.total ? (clProgress.done / clProgress.total) * 100 : 0}%`, transition: "width 0.3s" }} />
                  </div>
                </div>
              )}

              {clResults && checklistAnalytics && (
                <>
                  <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 3, marginBottom: 20, overflow: "hidden", boxShadow: "0 1px 8px rgba(0,0,0,0.05)" }}>
                    <div style={{ padding: "12px 20px", borderBottom: `1px solid ${C.border}`, background: C.bg, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
                      <div style={{ fontFamily: FONT_DATA, fontSize: 10, fontWeight: 600, letterSpacing: "0.18em", textTransform: "uppercase", color: C.muted }}>
                        Checklist Scan — {clResults.years.join(", ")} · {new Date(clResults.scannedAt).toLocaleString()}
                      </div>
                      <button
                        onClick={() => downloadChecklistCSV(checklistTableRows, nameIndex, clResults.years.join("-"))}
                        style={{ ...S.btnPrimary, padding: "6px 14px", fontSize: 11, background: C.gold, color: C.ink }}
                      >
                        ↓ Export CSV (Confidential)
                      </button>
                    </div>

                    <div style={{ padding: "16px 20px" }}>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 16 }}>
                        <div style={S.statBox}>
                          <div style={{ fontFamily: FONT_DISPLAY, fontSize: "1.6rem", fontWeight: 700, color: C.navy }}>{checklistAnalytics.total}</div>
                          <div style={{ fontFamily: FONT_DATA, fontSize: 9, letterSpacing: "0.1em", textTransform: "uppercase", color: C.muted }}>Applications</div>
                        </div>
                        <div style={S.statBox}>
                          <div style={{ fontFamily: FONT_DISPLAY, fontSize: "1.6rem", fontWeight: 700, color: C.red }}>{checklistAnalytics.withMissing}</div>
                          <div style={{ fontFamily: FONT_DATA, fontSize: 9, letterSpacing: "0.1em", textTransform: "uppercase", color: C.muted }}>With Missing Items</div>
                        </div>
                        <div style={S.statBox}>
                          <div style={{ fontFamily: FONT_DISPLAY, fontSize: "1.6rem", fontWeight: 700, color: C.green }}>{checklistAnalytics.complete}</div>
                          <div style={{ fontFamily: FONT_DATA, fontSize: 9, letterSpacing: "0.1em", textTransform: "uppercase", color: C.muted }}>Fully Complete</div>
                        </div>
                        <div style={S.statBox}>
                          <div style={{ fontFamily: FONT_DISPLAY, fontSize: "1.6rem", fontWeight: 700, color: C.gold }}>{checklistAnalytics.completionRate.toFixed(0)}%</div>
                          <div style={{ fontFamily: FONT_DATA, fontSize: 9, letterSpacing: "0.1em", textTransform: "uppercase", color: C.muted }}>Required Items Done</div>
                        </div>
                      </div>

                      {checklistAnalytics.topMissing.length > 0 && (
                        <div>
                          <div style={{ fontFamily: FONT_DATA, fontSize: 9, fontWeight: 600, letterSpacing: "0.15em", textTransform: "uppercase", color: C.muted, marginBottom: 8 }}>
                            Most Commonly Missing
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                            {checklistAnalytics.topMissing.map((item, i) => {
                              const max = checklistAnalytics.topMissing[0].count || 1;
                              return (
                                <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                  <div style={{ fontFamily: FONT_DATA, fontSize: 12, color: C.ink, width: 200, flexShrink: 0 }}>{item.desc}</div>
                                  <div style={{ flex: 1, height: 16, background: C.bg, borderRadius: 2, overflow: "hidden" }}>
                                    <div style={{ height: "100%", width: `${(item.count / max) * 100}%`, background: C.navy }} />
                                  </div>
                                  <div style={{ fontFamily: FONT_DATA, fontSize: 12, fontWeight: 700, color: C.navy, width: 36, textAlign: "right" }}>{item.count}</div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {clResults.errorCount > 0 && (
                        <div style={{ marginTop: 14, fontFamily: FONT_DATA, fontSize: 11, color: C.red, fontStyle: "italic" }}>
                          Note: {clResults.errorCount} application(s) failed to fetch and are shown with 0 items — re-run the scan to retry.
                        </div>
                      )}
                    </div>
                  </div>

                  <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 3, overflow: "hidden", boxShadow: "0 1px 8px rgba(0,0,0,0.05)" }}>
                    <div style={{ padding: "12px 20px", borderBottom: `1px solid ${C.border}`, background: C.bg, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
                      <div style={{ fontFamily: FONT_DATA, fontSize: 10, fontWeight: 600, letterSpacing: "0.18em", textTransform: "uppercase", color: C.muted }}>
                        Applicants — {checklistTableRows.length} shown
                      </div>
                      <label style={{ fontFamily: FONT_DATA, fontSize: 11, color: C.muted, display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                        <input type="checkbox" checked={clShowComplete} onChange={e => setClShowComplete(e.target.checked)} style={{ accentColor: C.navy }} />
                        Show fully-complete applicants
                      </label>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "24px 1.8fr 0.7fr 1fr 0.8fr 0.7fr", gap: 12, padding: "8px 14px", borderBottom: `1px solid ${C.border}`, background: "#fdfcf8", fontFamily: FONT_DATA, fontSize: 9, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: C.muted }}>
                      <div />
                      {[["name", "Applicant"], ["grade", "Grade"], ["status", "Status"]].map(([k, lbl]) => (
                        <div key={k} onClick={() => toggleClSort(k)} style={{ cursor: "pointer", userSelect: "none" }}>
                          {lbl}{clSortKey === k ? (clSortDir === "asc" ? " ▲" : " ▼") : ""}
                        </div>
                      ))}
                      <div>Required</div>
                      <div onClick={() => toggleClSort("missing")} style={{ cursor: "pointer", userSelect: "none" }}>
                        Status{clSortKey === "missing" ? (clSortDir === "asc" ? " ▲" : " ▼") : ""}
                      </div>
                    </div>

                    <div style={{ maxHeight: 520, overflowY: "auto" }}>
                      {checklistTableRows.length === 0 ? (
                        <div style={{ padding: 24, textAlign: "center", fontFamily: FONT_BODY, fontStyle: "italic", color: C.muted }}>
                          {clShowComplete ? "No applications in scan." : "All scanned applicants have complete checklists."}
                        </div>
                      ) : (
                        checklistTableRows.map((row, i) => (
                          <ChecklistRow key={`${row.applicationId}-${i}`} row={row} nameIndex={nameIndex} />
                        ))
                      )}
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}